import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { ArrowRight, Volume2 } from 'lucide-react';
import storage from 'local-storage-fallback';

import { useUserInfo } from '../hooks/useUserInfo';
import { CharacterCreationSuccessDialog } from '../components/CharacterCreationSuccessDialog';
import { supabase } from '../lib/supabase';
import { EhUpgradeDialog } from '../components/EhUpgradeDialog';
import EhAuthDialog from '@/components/EhAuthDialog';
import type { Database } from '../types/database';
import { getMediaUrl } from '@/lib/utils';
import { useTracking, UI_EVENTS, CHARACTER_EVENTS, PAYMENT_EVENTS } from '@/hooks/useTracking';
import { LS_NEW_CHAR_KEY } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { useGemBalance } from '@/hooks/useGemBalance';
import { SEO } from '@/components/SEO';

const TOTAL_STEPS = 8;
const CUSTOM_CHAR_COST = 10;
const FREE_CHAR_LIMIT = 1;
const FREE_PROFILE_IMAGE_LIMIT = 2;

const stepTitles = [
  'Gender & Style',
  'Ethnicity & Age',
  'Eyes & Hair',
  'Body',
  'Personality & Relationship',
  'Occupation & Hobbies',
  'Outfit & Voice',
  'Review',
];

type OptionsType = {
  value: string,
  label: string,
  imgPath?: string,
  mImgPath?: string,
  locked?: boolean,
  isDefault?: boolean,
  fOnly?: boolean,
  mOnly?: boolean
};

const GENDER_OPTIONS = [
  { value: 'f', label: 'Female', icon: '♀️', isDefault: true },
  { value: 'm', label: 'Male', icon: '♂️', locked: false },
];
const STYLE_OPTIONS = [
  { value: 'realistic', label: 'Realistic', imgPath: 'f/eh-cr-f-real.webp', mImgPath: 'm/eh-cr-m-real.webp', isDefault: true },
  { value: 'anime', label: 'Anime', imgPath: 'f/eh-cr-f-anime.webp', mImgPath: 'm/eh-cr-m-anime.webp', locked: false },
];

const ETHNICITY_OPTIONS = [
  { value: 'caucasian', label: 'Caucasian', imgPath: 'f/eh-cr-f-ethn-caucasian.webp', mImgPath: 'm/eh-cr-m-ethn-caucasian.webp' },
  { value: 'hispanic', label: 'Hispanic', imgPath: 'f/eh-cr-f-ethn-hispanic.webp', mImgPath: 'm/eh-cr-m-ethn-hispanic.webp' },
  { value: 'east_asian', label: 'East Asian', imgPath: 'f/eh-cr-f-ethn-asian.webp', mImgPath: 'm/eh-cr-m-ethn-asian.webp' },
  { value: 'indian', label: 'Indian', imgPath: 'f/eh-cr-f-ethn-indian.webp', mImgPath: 'm/eh-cr-m-ethn-indian.webp', locked: true },
  { value: 'middle_eastern', label: 'Middle Eastern', imgPath: 'f/eh-cr-f-ethn-middle-eastern.webp', mImgPath: 'm/eh-cr-m-ethn-middle-eastern.webp', locked: true },
  { value: 'black', label: 'Black', imgPath: 'f/eh-cr-f-ethn-black.webp', mImgPath: 'm/eh-cr-m-ethn-black.webp', locked: true },
  { value: 'mixed', label: 'Mixed', imgPath: 'f/eh-cr-f-ethn-mixed.webp', mImgPath: 'm/eh-cr-m-ethn-mixed.webp', locked: true },
  { value: 'random', label: 'Random', imgPath: 'eh-cr-random-8ball.webp', mImgPath: 'eh-cr-random-8ball.webp',   isDefault: true },
];
const AGE_OPTIONS = [
  { value: '18', label: 'Teen (18+)', locked: true },
  { value: '20s', label: '20s', isDefault: true },
  { value: '30s', label: '30s' },
  { value: '40s', label: '40s', locked: true },
  { value: '50s', label: '50s', locked: true },
  { value: '60+', label: '60+', locked: true },
];

const EYE_COLOR_OPTIONS = [
  { value: 'brown', label: 'Brown', imgPath: 'f/eh-cr-f-eyes-brown.webp', mImgPath: 'm/eh-cr-m-eyes-brown.webp', isDefault: true },
  { value: 'blue', label: 'Blue', imgPath: 'f/eh-cr-f-eyes-blue.webp', mImgPath: 'm/eh-cr-m-eyes-blue.webp' },
  { value: 'green', label: 'Green', imgPath: 'f/eh-cr-f-eyes-green.webp', mImgPath: 'm/eh-cr-m-eyes-green.webp', locked: true },
  { value: 'hazel', label: 'Hazel', imgPath: 'f/eh-cr-f-eyes-hazel.webp', mImgPath: 'm/eh-cr-m-eyes-hazel.webp', locked: true },
];
const HAIR_STYLE_OPTIONS = [
  { value: 'straight', label: 'Straight', imgPath: 'f/eh-cr-f-hair-straight.webp', fOnly: true },
  { value: 'fade', label: 'Fade', mImgPath: 'm/eh-cr-m-hair-fade.webp', locked: true, mOnly: true },
  { value: 'braids', label: 'Braids', imgPath: 'f/eh-cr-f-hair-braids.webp', locked: true, fOnly: true },
  { value: 'bangs', label: 'Bangs', imgPath: 'f/eh-cr-f-hair-bangs.webp', locked: true, fOnly: true },
  { value: 'short', label: 'Short', imgPath: 'f/eh-cr-f-hair-short.webp', mImgPath: 'm/eh-cr-m-hair-short.webp' },
  { value: 'long', label: 'Long', imgPath: 'f/eh-cr-f-hair-long.webp', mImgPath: 'm/eh-cr-m-hair-long-2.webp', isDefault: true },
  { value: 'curly', label: 'Curly', imgPath: 'f/eh-cr-f-hair-curly.webp', mImgPath: 'm/eh-cr-m-hair-curly.webp', locked: true, fOnly: true },
  { value: 'wavy', label: 'Wavy', imgPath: 'f/eh-cr-f-hair-wavy.webp', fOnly: true },
  { value: 'bun', label: 'Bun', imgPath: 'f/eh-cr-f-hair-bun.webp', locked: true, fOnly: true },
  { value: 'ponytail', label: 'Ponytail', imgPath: 'f/eh-cr-f-hair-ponytail.webp', locked: true, fOnly: true },
  { value: 'pigtails', label: 'Pigtails', imgPath: 'f/eh-cr-f-hair-pigtails.webp', locked: true, fOnly: true },
  { value: 'bald', label: 'Bald', mImgPath: 'm/eh-cr-m-hair-bald.webp', locked: true, mOnly: true },
];
const HAIR_COLOR_OPTIONS = [
  { value: 'blonde', label: 'Blonde', imgPath: 'f/eh-cr-f-hair-color-blonde.webp', mImgPath: 'm/eh-cr-m-hair-color-blonde.webp' },
  { value: 'brunette', label: 'Brunette', imgPath: 'f/eh-cr-f-hair-color-brunette.webp', mImgPath: 'm/eh-cr-m-hair-color-brunette.webp' },
  { value: 'black', label: 'Black', imgPath: 'f/eh-cr-f-hair-color-black.webp', mImgPath: 'm/eh-cr-m-hair-color-black.webp', isDefault: true },
  { value: 'red', label: 'Red', imgPath: 'f/eh-cr-f-hair-color-red.webp', mImgPath: 'm/eh-cr-m-hair-color-red.webp', locked: true },
  { value: 'pink', label: 'Pink', imgPath: 'f/eh-cr-f-hair-color-pink.webp', locked: true, fOnly: true },
  { value: 'gray', label: 'Gray', imgPath: 'f/eh-cr-f-hair-color-gray.webp', mImgPath: 'm/eh-cr-m-hair-color-gray.webp', locked: true },
];

const BODY_TYPE_OPTIONS = [
  { value: 'slim', label: 'Slim', imgPath: 'f/eh-cr-f-body-slim.webp', mImgPath: 'm/eh-cr-m-body-slim.webp', locked: true, fOnly: true },
  { value: 'athletic', label: 'Athletic', imgPath: 'f/eh-cr-f-body-athletic.webp', mImgPath: 'm/eh-cr-m-body-athletic.webp' },
  { value: 'normal', label: 'Normal', imgPath: 'f/eh-cr-f-body-normal.webp', mImgPath: 'm/eh-cr-m-body-normal.webp', isDefault: true },
  { value: 'voluptuous', label: 'Voluptuous', imgPath: 'f/eh-cr-f-body-voluptuous.webp', locked: true, fOnly: true },
  { value: 'muscular', label: 'Muscular', mImgPath: 'm/eh-cr-m-body-muscular.webp', mOnly: true },
  { value: 'curvy', label: 'Curvy', imgPath: 'f/eh-cr-f-body-curvy.webp', locked: true, fOnly: true },
  { value: 'plump', label: 'Plump', mImgPath: 'm/eh-cr-m-body-plump.webp', locked: true, mOnly: true },
];
const BUST_SIZE_OPTIONS = [
  { value: 'small', label: 'Small', imgPath: 'f/eh-cr-f-bust-small.webp', locked: true },
  { value: 'medium', label: 'Medium', imgPath: 'f/eh-cr-f-bust-medium.webp', isDefault: true },
  { value: 'big', label: 'Big', imgPath: 'f/eh-cr-f-bust-big.webp' },
  { value: 'huge', label: 'Huge', imgPath: 'f/eh-cr-f-bust-huge.webp', locked: true },
].map(opt => ({ ...opt, fOnly: true }));
const BUTT_SIZE_OPTIONS = [
  { value: 'small', label: 'Small', imgPath: 'f/eh-cr-f-butt-small.webp', locked: true },
  { value: 'medium', label: 'Medium', imgPath: 'f/eh-cr-f-butt-medium.webp', isDefault: true },
  { value: 'large', label: 'Large', imgPath: 'f/eh-cr-f-butt-large.webp' },
  { value: 'huge', label: 'Huge', imgPath: 'f/eh-cr-f-butt-huge.webp', locked: true },
].map(opt => ({ ...opt, fOnly: true }));

const PERSONALITY_OPTIONS = [
  { value: 'caregiver', label: 'Caregiver', emoji: '🫶', desc: 'Always there to offer some affection and comfort.' },
  { value: 'wise', label: 'Wise', emoji: '🧘‍♀️', desc: 'A source of wisdom.', locked: false },
  { value: 'ambitious', label: 'Ambitious', emoji: '🧗', desc: 'Loves challenges and always wants to be the best.' },
  { value: 'amusing', label: 'Amusing', emoji: '🤹', desc: 'Cheerful, funny, and always there to make you laugh.' },
  { value: 'seducer', label: 'Seducer', emoji: '💋', desc: 'Loves to seduce, tease and turn you on.', isDefault: true },
  { value: 'dominant', label: 'Dominant', emoji: '👑', desc: 'Wants to be in control.' },
  { value: 'submissive', label: 'Submissive', emoji: '🫡', desc: 'Happy to be lead by a strong partner.' },
  { value: 'romantic', label: 'Romantic', emoji: '💗', desc: 'Longs for a deep emotional connection and affection.' }, 
  { value: 'insatiable', label: 'Insatiable', emoji: '🔥', desc: 'Always looking for intimacy and passionate contact.' },
  { value: 'bully', label: 'Bully', emoji: '🥊', desc: 'Intimidating, often sarcastic and dismissive.' },
  { value: 'rebellious', label: 'Rebellious', emoji: '✊', desc: 'Disputes authority, loves like-minded people.' },
  { value: 'mysterious', label: 'Mysterious', emoji: '🔒', desc: 'Full of secrets and mysteries.' },
];
const RELATIONSHIP_OPTIONS = [
  { value: 'stranger', label: 'Stranger', emoji: '👋' },
  { value: 'school mate', label: 'School Mate', emoji: '🎓' },
  { value: 'colleague', label: 'Colleague', emoji: '💼' },
  { value: 'mentor', label: 'Mentor', emoji: '📚' },
  { value: 'girlfriend', label: 'Girlfriend', emoji: '❤️', fOnly: true },
  { value: 'sex friend', label: 'Friends with Benefits', emoji: '⚤', isDefault: true },
  { value: 'wife', label: 'Wife', emoji: '💍', fOnly: true },
  { value: 'mistress', label: 'Mistress', emoji: '👠', fOnly: true },
  { value: 'friend', label: 'Friend', emoji: '🤝' },
  { value: 'best friend', label: 'Best Friend', emoji: '🤗' },
  { value: 'step sister', label: 'Step Sister', emoji: '🛑', locked: false, fOnly: true },
  { value: 'step brother', label: 'Step Brother', emoji: '🛑', locked: false, mOnly: true },
  { value: 'step mom', label: 'Step Mom', emoji: '🔥', fOnly: true },
  { value: 'ex wife', label: 'Ex-Wife', emoji: '💔', fOnly: true },
  { value: 'ex girlfriend', label: 'Ex-Girlfriend', emoji: '❤️‍🩹', fOnly: true },
  { value: 'boyfriend', label: 'Boyfriend', emoji: '❤️', mOnly: true },
  { value: 'ex-husband', label: 'Ex-Husband', emoji: '💔', mOnly: true },
  { value: 'ex-boyfriend', label: 'Ex-Boyfriend', emoji: '❤️‍🩹', mOnly: true },
  { value: 'online connection', label: 'Online Connection', emoji: '🌐' },
  { value: 'long distance', label: 'Long Distance', emoji: '✈️' },
  { value: 'long-time crush', label: 'Long-time Crush', emoji: '😍' },
  { value: 'personal trainer', label: 'Personal Trainer', emoji: '🏋️' },
  { value: 'vacation fling', label: 'Vacation Fling', emoji: '🏖️' },
];

const OCCUPATION_OPTIONS = [
  { value: 'fitness instructor', label: 'Fitness Instructor' },
  { value: 'dancer', label: 'Dancer' },
  { value: 'fashion model', label: 'Fashion Model', fOnly: true, isDefault: true },
  { value: 'actor', label: 'Actor', mOnly: true },
  { value: 'actress', label: 'Actress', fOnly: true },
  { value: 'stay-at-home', label: 'Stay-at-home' },
  { value: 'lawyer', label: 'Lawyer' },
  { value: 'musician', label: 'Musician' },
  { value: 'photographer', label: 'Photographer' },
  { value: 'flight attendant', label: 'Flight Attendant' },
  { value: 'student', label: 'Student' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'cook', label: 'Cook' },
  { value: 'maid', label: 'Maid', fOnly: true },
  { value: 'construction_worker', label: 'Construction Worker' },
  { value: 'mechanic', label: 'Mechanic' },
  { value: 'police officer', label: 'Police Officer' },
  { value: 'nurse', label: 'Nurse', fOnly: true },
  { value: 'doctor', label: 'Doctor' },
  { value: 'gynecologist', label: 'Gynecologist', locked: true },
  { value: 'writer', label: 'Writer' },
  { value: 'blogger', label: 'Blogger' },
  { value: 'florist', label: 'Florist' },
  { value: 'pornstar', label: 'Pornstar', locked: true },
  { value: 'onlyfans model', label: 'OnlyFans Model', locked: true, fOnly: true },
  { value: 'librarian', label: 'Librarian' },
  { value: 'nun', label: 'Nun', fOnly: true },
  { value: 'spy', label: 'Spy' },
];
const HOBBY_OPTIONS = [
  { value: 'dance', label: 'Dance' },
  { value: 'hiking', label: 'Hiking' },
  { value: 'tennis', label: 'Tennis' },
  { value: 'golfing', label: 'Golfing' },
  { value: 'cooking', label: 'Cooking' },
  { value: 'reading', label: 'Reading' },
  { value: 'martial arts', label: 'Martial Arts' },
  { value: 'yoga', label: 'Yoga', locked: false },
  { value: 'surfing', label: 'Surfing' },
  { value: 'photography', label: 'Photography' },
  { value: 'video games', label: 'Video Games' },
  { value: 'watching netflix', label: 'Watching Netflix' },
  { value: 'travel', label: 'Travel', isDefault: true },
  { value: 'anime', label: 'Anime' },
  { value: 'partying', label: 'Partying' },
  { value: 'social media', label: 'Social Media' },
  { value: 'wine tasting', label: 'Wine Tasting' },
  { value: 'weightlifting', label: 'Weightlifting' },
  { value: 'veganism', label: 'Veganism' },
];

const OUTFIT_OPTIONS = [
  { value: 'bikini', label: 'Bikini', fOnly: true },
  { value: 'evening dress', label: 'Evening Dress', fOnly: true },
  { value: 'summer dress', label: 'Summer Dress', fOnly: true },
  { value: 'wedding dress', label: 'Wedding Dress', fOnly: true },
  { value: 'suit', label: 'Suit', mOnly: true },
  { value: 'cheerleader', label: 'Cheerleader', fOnly: true },
  { value: 'skirt', label: 'Skirt', fOnly: true },
  { value: 'swimsuit', label: 'Swimsuit' },
  { value: 'lab coat', label: 'Lab Coat' },
  { value: 'pyjamas', label: 'Pyjamas' },
  { value: 'carnival', label: 'Carnival' },
  { value: 'casual', label: 'Casual', isDefault: true },
  { value: 'flight attendant', label: 'Flight Attendant' },
  { value: 'corset', label: 'Corset', fOnly: true },
  { value: 'leggings', label: 'Leggings', fOnly: true },
  { value: 'yoga outfit', label: 'Yoga Outfit' },
  { value: 'lingerie', label: 'Lingerie', fOnly: true },
  { value: 'leather outfit', label: 'Leather Outfit' },
  { value: 'police', label: 'Police' },
  { value: 'steampunk', label: 'Steampunk' },
  { value: 'firefighter', label: 'Firefighter' },
  { value: 'military', label: 'Military' },
  { value: 'construction_worker', label: 'Construction Worker' },
  { value: 'belly dancer', label: 'Belly Dancer', locked: false, fOnly: true },
  { value: 'hiphop', label: 'Hiphop' },
  { value: 'hijab', label: 'Hijab', fOnly: true },
  { value: 'secretary', label: 'Secretary', fOnly: true },
];

const VOICE_OPTIONS = [
  // { value: 'f_e_american-c', label: 'American', fOnly: true, isDefault: true },
  // { value: 'f_e_american-casual', label: 'American Casual', fOnly: true },
  { value: 'f_e_american-african', label: 'African American', fOnly: true },
  { value: 'f_e_american-aussie', label: 'American', fOnly: true, isDefault: true },
  // { value: 'f_e_american-highpitch', label: 'American, High pitch', fOnly: true, locked: true },
  // { value: 'f_e_american-intimate', label: 'American, Intimate', fOnly: true, locked: true },
  // { value: 'f_e_american-ny', label: 'American, NY', fOnly: true, locked: true },
  { value: 'f_e_american-scandinavian', label: 'American / Scandinavian', fOnly: true },
  // { value: 'f_e_american-slow', label: 'American, Slow', fOnly: true },
  { value: 'f_e_american-dynamic', label: 'American, Dynamic', fOnly: true },
  { value: 'f_e_american-young', label: 'American, Young', fOnly: true, locked: true },
  { value: 'f_e_arabic', label: 'Arabic', fOnly: true, locked: true },
  // { value: 'f_e_british-c', label: 'British', fOnly: true },
  { value: 'f_e_british', label: 'British', fOnly: true },
  { value: 'f_e_chinese', label: 'Chinese', fOnly: true },
  // { value: 'f_e_french', label: 'French', fOnly: true },
  // { value: 'f_e_german', label: 'German', fOnly: true },
  { value: 'f_e_hindi', label: 'Hindi', fOnly: true },
  { value: 'f_e_japanese', label: 'Japanese', fOnly: true },
  { value: 'f_e_russian', label: 'Russian', fOnly: true, locked: true },
  { value: 'f_e_spanish', label: 'Spanish, Colombian', fOnly: true },

  // Male voices
  { value: 'm_e_american', label: 'American', mOnly: true, isDefault: true },
  // { value: 'm_e_american-c', label: 'American', mOnly: true },
  { value: 'm_e_american-deep', label: 'American, Deep', mOnly: true },
  { value: 'm_e_american-young', label: 'American, Young', mOnly: true, locked: true },
  // { value: 'm_e_american-young-c', label: 'American, Young', mOnly: true },
  { value: 'm_e_australian', label: 'Australian', mOnly: true },
  { value: 'm_e_british', label: 'British', mOnly: true },
  // { value: 'm_e_australian-c', label: 'Australian', mOnly: true },
  // { value: 'm_e_british-c', label: 'British', mOnly: true },
  // { value: 'm_e_hindi', label: 'Hindi', mOnly: true, locked: true },
  // { value: 'm_e_italian', label: 'Italian', mOnly: true, locked: true },
  { value: 'm_e_portuguese', label: 'Portuguese', mOnly: true, locked: true },
  // { value: 'm_e_scottish', label: 'Scottish', mOnly: true },
  { value: 'm_e_spanish', label: 'Spanish', mOnly: true },
];

const optionsMap: Record<string, OptionsType[]> = {
  style: STYLE_OPTIONS,
  ethnicity: ETHNICITY_OPTIONS,
  age: AGE_OPTIONS,
  eye_color: EYE_COLOR_OPTIONS,
  hair_style: HAIR_STYLE_OPTIONS,
  hair_color: HAIR_COLOR_OPTIONS,
  body_type: BODY_TYPE_OPTIONS,
  bust_size: BUST_SIZE_OPTIONS,
  butt_size: BUTT_SIZE_OPTIONS,
  personality: PERSONALITY_OPTIONS,
  relationship: RELATIONSHIP_OPTIONS,
  occupation: OCCUPATION_OPTIONS,
  hobbies: HOBBY_OPTIONS,
  outfit: OUTFIT_OPTIONS,
  voice: VOICE_OPTIONS,
}

interface CharacterState {
  current_step: number;
  updated_at: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
};

function getInitialState(): CharacterState {
  if (typeof window !== 'undefined') {
    const saved = storage.getItem(LS_NEW_CHAR_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        if (import.meta.env.DEV) console.log('Failed to load character state from localStorage', saved);
      }
    }
  }
  return {
    current_step: 0,
    updated_at: Date.now(),
    attributes: {},
  };
}

export default function CreateCharacterPage() {
  const navigate = useNavigate();
  const { characters, isSubscriber, isLoading: isLoadingUserInfo, refetchCharacters, user } = useUserInfo();
  const { balance, refreshBalance } = useGemBalance();
  const { trackEvent } = useTracking();

  const [state, setState] = useState<CharacterState>(getInitialState());
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showPersonalityCustom, setShowPersonalityCustom] = useState(false);
  const [showRelationshipCustom, setShowRelationshipCustom] = useState(false);
  const [showOccupationCustom, setShowOccupationCustom] = useState(false);
  const [showHobbiesCustom, setShowHobbiesCustom] = useState(false);
  const [showOutfitCustom, setShowOutfitCustom] = useState(false);
  const [personalityCustom, setPersonalityCustom] = useState('');
  const [relationshipCustom, setRelationshipCustom] = useState('');
  const [occupationCustom, setOccupationCustom] = useState('');
  const [hobbiesCustom, setHobbiesCustom] = useState('');
  const [outfitCustom, setOutfitCustom] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [characterCreationError, setCharacterCreationError] = useState('');
  const [createdCharacter, setCreatedCharacter] = useState<Database['eh']['Tables']['characters']['Row'] | null>(null);
  const [imageStatus, setImageStatus] = useState<'idle'|'pending'|'success'|'failed'>('idle');
  const [avatarUrl, setAvatarUrl] = useState<string|null>(null);
  // const [imageRequestId, setImageRequestId] = useState<string|null>(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showGemBalanceMsg, setShowGemBalanceMsg] = useState(false);

  const imageRetryCountRef = useRef(0);
  const imageRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const authDialogOnCloseHandlerRef = useRef<((open: boolean, { user, onboardingCompleted }: {
    user?: User,
    onboardingCompleted?: boolean
  }) => void)>((open, _data) => { setShowAuthDialog(open); });

  // Sync to localStorage
  useEffect(() => {
    storage.setItem(LS_NEW_CHAR_KEY, JSON.stringify(state));
  }, [state]);

  // Step navigation
  const goToStep = (step: number) => {
    stopAllVoiceSamples(); // Stop any playing voice samples when changing steps
    setState((s: CharacterState) => ({ ...s, current_step: step, updated_at: Date.now() }));
    if (titleRef.current) {
      titleRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const nextStep = () => goToStep(Math.min(state.current_step + 1, TOTAL_STEPS - 1));
  const prevStep = () => goToStep(Math.max(state.current_step - 1, 0));

  // --- Step 1: Gender & Style ---
  const handleSelect = (key: string, value: string | string[]) => {
    setState(s => {
      // If gender changes, reset all attributes except gender
      if (key === 'gender' && s.attributes.gender !== value) {
        const newGender = value as string;
        return {
          ...s,
          attributes: { // reset all attribute options incompatible with new gender, except hobbies
            ...Object.fromEntries(
              Object.entries(s.attributes).filter(
                ([k, v]) => k === 'hobbies'
                  || optionsMap[k] && genderFilterWithNewGender(optionsMap[k].find(o => o.value === v) || { missing: true }, newGender)
              )
            ),
            gender: newGender
          },
          updated_at: Date.now(),
        };
      }
      return {
        ...s,
        attributes: { ...s.attributes, [key]: value },
        updated_at: Date.now(),
      };
    });
  };

  const genderFilter = (opt: { mOnly?: boolean, fOnly?: boolean }) => {
    return (opt.fOnly && gender === 'f')
      || (opt.mOnly && gender === 'm')
      || (!opt.fOnly && !opt.mOnly);
  };
  const genderFilterWithNewGender = (opt: { missing?: boolean, mOnly?: boolean, fOnly?: boolean }, newGender: string) => {
    // if (import.meta.env.DEV) console.log('[DEV] genderFilterWithNewGender', opt, newGender);
    return (opt.fOnly && newGender === 'f')
      || (opt.mOnly && newGender === 'm')
      || (!opt.missing && !opt.fOnly && !opt.mOnly);
  };

  const gender = state.attributes.gender;
  const style = state.attributes.style;
  const ethnicity = state.attributes.ethnicity;
  const age = state.attributes.age;
  const body_type = state.attributes.body_type;
  const bust_size = state.attributes.bust_size;
  const butt_size = state.attributes.butt_size;
  const personality = state.attributes.personality;
  const relationship = state.attributes.relationship;
  const occupation = state.attributes.occupation;
  const hobbies = state.attributes.hobbies || [];
  const outfit = state.attributes.outfit;
  const voice = state.attributes.voice;

  // Voice sample playing state
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const voiceAudioRefs = useRef<Record<string, HTMLAudioElement>>({});
  const voicePlayPromiseRefs = useRef<Record<string, Promise<void> | null>>({});

  // Play voice sample
  const playVoiceSample = async (voiceValue: string) => {
    // If the same voice is currently playing, stop it
    if (playingVoice === voiceValue) {
      const audio = voiceAudioRefs.current[voiceValue];
      const playPromise = voicePlayPromiseRefs.current[voiceValue];
      
      if (audio && playPromise) {
        // Wait for the play promise to resolve before pausing
        try {
          await playPromise;
          audio.pause();
        } catch (_err) {
          // Play was already rejected, safe to pause
          audio.pause();
        }
      } else if (audio) {
        audio.pause();
      }
      
      setPlayingVoice(null);
      voicePlayPromiseRefs.current[voiceValue] = null;
      return;
    }

    // Stop any currently playing audio
    if (playingVoice) {
      const currentAudio = voiceAudioRefs.current[playingVoice];
      const currentPlayPromise = voicePlayPromiseRefs.current[playingVoice];
      
      if (currentAudio && currentPlayPromise) {
        try {
          await currentPlayPromise;
          currentAudio.pause();
        } catch (_err) {
          currentAudio.pause();
        }
      } else if (currentAudio) {
        currentAudio.pause();
      }
      
      voicePlayPromiseRefs.current[playingVoice] = null;
    }

    // Create or reuse audio element
    let audio = voiceAudioRefs.current[voiceValue];
    if (!audio) {
      audio = new Audio(`/voices/${voiceValue}.mp3`);
      voiceAudioRefs.current[voiceValue] = audio;
      
      audio.onended = () => {
        if (playingVoice === voiceValue) setPlayingVoice(null);
        voicePlayPromiseRefs.current[voiceValue] = null;
      };
      
      audio.onerror = () => {
        if (playingVoice === voiceValue) setPlayingVoice(null);
        voicePlayPromiseRefs.current[voiceValue] = null;
      };
    }

    // Reset audio to beginning
    audio.currentTime = 0;
    setPlayingVoice(voiceValue);
    
    // Play audio with proper promise handling
    const playPromise = audio.play();
    voicePlayPromiseRefs.current[voiceValue] = playPromise;
    
    playPromise.catch(() => {
      setPlayingVoice(null);
      voicePlayPromiseRefs.current[voiceValue] = null;
    });
  };

  // Timeline UI
  const renderTimeline = () => (
    <div className={`max-w-full flex items-center justify-center gap-2 p-1 mb-2 overflow-hidden select-none ${isSubmitting ? 'hidden' : ''}`}>
      {stepTitles.map((title, idx) => (
        <button
          key={title}
          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white transition-all ${
            idx === state.current_step
              ? 'bg-pink-600 scale-110' : idx < state.current_step
              ? 'bg-pink-400' : 'bg-zinc-700'
          }`}
          onClick={() => idx <= state.current_step && goToStep(idx)}
          disabled={idx > state.current_step}
        >
          {idx + 1}
        </button>
      ))}
    </div>
  );

  // Step 1 UI
  const renderStep1 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Gender</div>
        <div className="flex gap-4 justify-center">
          {GENDER_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-28 h-32 overflow-hidden rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all bg-zinc-900 ${
                  gender === opt.value ? 'border-pink-500' : 'border-zinc-700'
                }`}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('gender', opt.value)}
                type="button"
              >
                <span className="text-4xl mb-8">{opt.icon}</span>
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md text-white text-sm font-medium">
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Style</div>
        <div className="flex gap-4 justify-center">
          {STYLE_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-52 h-76 overflow-hidden rounded-lg select-none flex flex-col items-center justify-end border-2 transition-all
                  ${style === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('style', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white text-md font-medium">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 2 UI
  const renderStep2 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Ethnicity</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {ETHNICITY_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${ethnicity === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? 'opacity-50' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('ethnicity', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-1.5 py-0.5 rounded-md text-white text-sm font-medium text-nowrap ${
                    locked ? 'hidden' : ''
                  }`}>
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Age</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {AGE_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-28 h-16 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${age === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? 'opacity-50' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('age', opt.value)}
                type="button"
              >
                <span className={`${locked ? 'blur-sm' : ''}`}>
                  <span className="text-lg font-bold text-white">{opt.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 3 UI
  const renderStep3 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Eye Color</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {EYE_COLOR_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${state.attributes.eye_color === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('eye_color', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Hair Style</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {HAIR_STYLE_OPTIONS
            .filter(genderFilter)
            .map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${state.attributes.hair_style === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('hair_style', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-2">Choose Hair Color</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {HAIR_COLOR_OPTIONS.filter(genderFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${state.attributes.hair_color === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('hair_color', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 4 UI
  const renderStep4 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3">Choose Body Type</div>
        <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
          {BODY_TYPE_OPTIONS.filter(genderFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                  ${body_type === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('body_type', opt.value)}
                type="button"
              >
                <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                  <img src={`/images/demo/${gender === 'm' ? opt.mImgPath : opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                    {opt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {gender === 'f' && (
        <div>
          <div className="text-white text-center font-semibold mb-3">Choose Bust Size</div>
          <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
            {BUST_SIZE_OPTIONS.map(opt => {
              const locked = opt.locked && !isSubscriber;
              return (
                <button
                  key={opt.value}
                  className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                    ${bust_size === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                    ${locked ? '' : 'hover:border-pink-400'}
                  `}
                  onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('bust_size', opt.value)}
                  type="button"
                >
                  <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                    <img src={`/images/demo/${opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                      {opt.label}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {gender === 'f' && (
        <div>
          <div className="text-white text-center font-semibold mb-3">Choose Butt Size</div>
          <div className="flex gap-2 sm:gap-4 justify-center flex-wrap">
            {BUTT_SIZE_OPTIONS.map(opt => {
              const locked = opt.locked && !isSubscriber;
              return (
                <button
                  key={opt.value}
                  className={`relative w-25 md:w-28 h-32 rounded-lg overflow-hidden select-none flex flex-col items-center justify-end border-2 transition-all
                    ${butt_size === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                    ${locked ? '' : 'hover:border-pink-400'}
                  `}
                  onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('butt_size', opt.value)}
                  type="button"
                >
                  <span className={`h-full w-full ${locked ? 'blur-sm' : ''}`}>
                    <img src={`/images/demo/${opt.imgPath}`} alt={opt.label} className="w-full h-full object-cover" />
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white font-medium text-sm">
                      {opt.label}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // Step 5 UI
  const renderStep5 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3 flex items-center justify-center gap-2">
          <span>Choose Personality</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowPersonalityCustom(v => !v)}>
            {showPersonalityCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showPersonalityCustom && (
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={3}
            value={personalityCustom}
            onChange={e => {
              setPersonalityCustom(e.target.value);
              handleSelect('personality', e.target.value);
            }}
            placeholder="Describe the personality..."
          />
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center">
          {PERSONALITY_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-full rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all p-4
                  ${personality === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : (handleSelect('personality', opt.value), setPersonalityCustom(opt.label))}
                type="button"
              >
                <span className={`text-2xl mb-2 ${locked ? 'blur-md' : ''}`}>{opt.emoji}</span>
                <span className={`font-bold text-white mb-1 ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
                <span className={`text-xs text-zinc-300 text-center ${locked ? 'blur-md' : ''}`}>{opt.desc}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3 flex items-center justify-center gap-2">
          <span>Choose Relationship</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowRelationshipCustom(v => !v)}>
            {showRelationshipCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showRelationshipCustom &&
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={2}
            value={relationshipCustom}
            onChange={e => {
              setRelationshipCustom(e.target.value);
              handleSelect('relationship', e.target.value);
            }}
            placeholder="Describe the relationship..."
          />
        }
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {RELATIONSHIP_OPTIONS.filter(genderFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative size-25 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${relationship === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : (handleSelect('relationship', opt.value), setRelationshipCustom(opt.label))}
                type="button"
              >
                <span className={`text-3xl mb-1 ${locked ? 'blur-md' : ''}`}>{opt.emoji}</span>
                <span className={`font-bold text-white text-sm ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 6 UI
  const renderStep6 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-3 flex items-center justify-center gap-2">
          <span>Choose Occupation</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowOccupationCustom(v => !v)}>
            {showOccupationCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showOccupationCustom &&
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={2}
            value={occupationCustom}
            onChange={e => {
              setOccupationCustom(e.target.value);
              handleSelect('occupation', e.target.value);
            }}
            placeholder="Describe the occupation..."
          />
        }
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {OCCUPATION_OPTIONS.filter(genderFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 h-14 px-2 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${occupation === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : (handleSelect('occupation', opt.value), setOccupationCustom(opt.label))}
                type="button"
              >
                <span className={`font-medium text-white text-sm ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-3 flex items-center justify-center gap-2">
          <span>Choose Hobbies (up to 3)</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowHobbiesCustom(v => !v)}>
            {showHobbiesCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showHobbiesCustom && (
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={2}
            value={hobbiesCustom}
            onChange={e => {
              setHobbiesCustom(e.target.value);
              handleSelect('hobbies', e.target.value.split(',').map(h => h.trim()).filter(Boolean));
            }}
            placeholder="List hobbies, separated by commas..."
          />
        )}
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {HOBBY_OPTIONS.map(opt => {
            const locked = opt.locked && !isSubscriber;
            const selected = hobbies.includes(opt.value);
            const disabled = !selected && hobbies.length >= 3;
            return (
              <button
                key={opt.value}
                className={`relative w-24 h-12 px-2 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${selected ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : !disabled ? 'hover:border-pink-400' : 'opacity-50'}
                `}
                onClick={() => {
                  if (locked) return setShowUpgradeDialog(true);
                  if (selected) {
                    const newHobbies = hobbies.filter((h: string) => h !== opt.value);
                    handleSelect('hobbies', newHobbies);
                    setHobbiesCustom(newHobbies.join(', '));
                  } else if (!disabled) {
                    const newHobbies = [...hobbies, opt.value];
                    handleSelect('hobbies', newHobbies);
                    setHobbiesCustom(newHobbies.join(', '));
                  }
                }}
                type="button"
                disabled={disabled && !selected}
              >
                <span className={`font-medium text-white text-xs ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 7 UI - Outfit & Voice
  const renderStep7 = () => (
    <div className="w-full flex flex-col gap-8">
      <div>
        <div className="text-white text-center font-semibold mb-6 flex items-center justify-center gap-2">
          <span className="text-2xl">Outfit</span>
          <button className="ml-2 px-2 py-1 text-xs rounded bg-zinc-700 text-white" type="button" onClick={() => setShowOutfitCustom(v => !v)}>
            {showOutfitCustom ? 'Hide' : 'Customize 📝'}
          </button>
        </div>
        {showOutfitCustom && (
          <textarea
            className="w-full mb-2 p-2 rounded bg-zinc-700 text-white"
            maxLength={1000}
            rows={2}
            value={outfitCustom}
            onChange={e => {
              setOutfitCustom(e.target.value);
              handleSelect('outfit', e.target.value);
            }}
            placeholder="Describe the outfit..."
          />
        )}
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {OUTFIT_OPTIONS.filter(genderFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            return (
              <button
                key={opt.value}
                className={`relative w-25 h-14 px-2 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                  ${outfit === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                  ${locked ? '' : 'hover:border-pink-400'}
                `}
                onClick={() => locked ? setShowUpgradeDialog(true) : (handleSelect('outfit', opt.value), setOutfitCustom(opt.label))}
                type="button"
              >
                <span className={`font-medium text-white text-sm ${locked ? 'blur-md' : ''}`}>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-white text-center font-semibold mb-6 text-2xl">Accent</div>
        <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
          {VOICE_OPTIONS.filter(genderFilter).map(opt => {
            const locked = opt.locked && !isSubscriber;
            const isPlaying = playingVoice === opt.value;
            return (
              <div key={opt.value} className="relative">
                <button
                  className={`relative w-28 h-24 px-2 pt-10 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all
                    ${voice === opt.value ? 'border-pink-500' : 'border-zinc-700'}
                    ${locked ? '' : 'hover:border-pink-400'}
                  `}
                  onClick={() => locked ? setShowUpgradeDialog(true) : handleSelect('voice', opt.value)}
                  type="button"
                >
                  <span className={`font-medium text-white text-sm ${locked ? 'blur-md' : ''}`}>{`${locked ? 'Locked voice' : opt.label}`}</span>
                </button>
                {/* Play button overlay */}
                <button
                  className={`absolute top-3 left-1/2 -translate-x-1/2 flex items-center justify-center size-8 rounded-full
                    bg-zinc-700/90 text-zinc-300 transition-colors z-10 ${
                      locked ? 'blur-sm hover:bg-zinc-700/90' : 'hover:bg-zinc-600/90 hover:text-white'
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!locked) playVoiceSample(opt.value);
                  }}
                  type="button"
                  title="Play voice sample"
                  aria-label="Play voice sample"
                >
                  <Volume2 className={`size-4 ${isPlaying ? 'text-pink-400' : ''}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Step 8 UI - Review
  const renderStep8 = () => {
    // Gather all attributes for display
    const summary = [
      { label: 'Gender', value: state.attributes.gender, options: GENDER_OPTIONS },
      { label: 'Style', value: state.attributes.style, options: STYLE_OPTIONS },
      { label: 'Ethnicity', value: state.attributes.ethnicity, options: ETHNICITY_OPTIONS },
      { label: 'Age', value: state.attributes.age, options: AGE_OPTIONS },
      { label: 'Eye Color', value: state.attributes.eye_color, options: EYE_COLOR_OPTIONS },
      { label: 'Hair Style', value: state.attributes.hair_style, options: HAIR_STYLE_OPTIONS },
      { label: 'Hair Color', value: state.attributes.hair_color, options: HAIR_COLOR_OPTIONS },
      { label: 'Body Type', value: state.attributes.body_type, options: BODY_TYPE_OPTIONS },
      { label: 'Bust Size', value: state.attributes.bust_size, options: BUST_SIZE_OPTIONS, show: state.attributes.gender === 'f' },
      { label: 'Butt Size', value: state.attributes.butt_size, options: BUTT_SIZE_OPTIONS, show: state.attributes.gender === 'f' },
      { label: 'Voice', value: state.attributes.voice, options: VOICE_OPTIONS },
    ];
    // Helper to get label from value
    const getLabel = (value: string, options: OptionsType[]) => options.find(o => o.value === value)?.label || value;
    const getImgPath = (value: string, options: OptionsType[], gender = 'f') =>
      options.find(o => o.value === value)?.[gender === 'm' ? 'mImgPath' : 'imgPath'] || '';
    // Required fields check
    const required = [
      'gender','style','ethnicity','age','eye_color','hair_style','hair_color','body_type','personality','relationship','occupation','hobbies','outfit','voice',
    ];
    if (state.attributes.gender === 'f') required.push('bust_size', 'butt_size');
    const missing = required.some(k => !state.attributes[k] || (k === 'hobbies' && (!state.attributes.hobbies || state.attributes.hobbies.length === 0)));

    const handleAuthDialogOnClose = (open: boolean, { user, onboardingCompleted }: {
      user?: User,
      onboardingCompleted?: boolean
    } = {}) => {
      setShowAuthDialog(false);
      if (!open && user && onboardingCompleted) handleConfirm();
    }

    const handleMediaRequest = async (customPrompt: string, resourceId: string, { requestId, successfulGenCount = 0 }: {
      requestId?: string,
      successfulGenCount?: number
    } = {}) => {
      if (successfulGenCount >= FREE_PROFILE_IMAGE_LIMIT && !isSubscriber) {
        setShowUpgradeDialog(true);
        return { success: false, userErrorMsg: 'Upgrade to create more profile images' };
      }
      setImageStatus('pending');

      const { data: mediaData, error: mediaError } = await supabase.functions.invoke('eh-media-request', {
        body: {
          customPrompt,
          style: state.attributes.style,
          requestId,
          resource_id: resourceId,
          media_type: 'profile_image',
        }
      });
      if (import.meta.env.DEV) console.log('[DEV] handleMediaRequest data:', mediaData);

      if (mediaError && imageRetryCountRef.current > 0 ) { // retry only once after error
        setImageStatus('failed');
        trackEvent(CHARACTER_EVENTS.NEW_PROFILE_IMAGE_ERROR, {
          error: mediaError,
          url_slug: createdCharacter?.url_slug,
          source: 'CreateCharacterPage',
        });
        return { success: false, userErrorMsg: 'Oops, something went wrong. Please try again.' };
      } else if (mediaError || (mediaData?.retryAfter && imageRetryCountRef.current < 3)) {
        imageRetryCountRef.current++;
        // retryAfter is an epoch timestamp, compute delay in ms
        const delay = mediaData?.retryAfter ? mediaData.retryAfter - Date.now() : 0;
        imageRetryTimeoutRef.current = setTimeout(async () => {
          await handleMediaRequest(customPrompt, resourceId, {
            requestId: mediaData?.requestId || undefined,
            successfulGenCount,
          });
        }, delay);
      } else if (!mediaData?.success || !mediaData?.filePath) {
        setImageStatus('failed');
        trackEvent(CHARACTER_EVENTS.NEW_PROFILE_IMAGE_ERROR, {
          error: `Missing data: ${JSON.stringify(mediaData)}`,
          url_slug: createdCharacter?.url_slug,
          source: 'CreateCharacterPage',
        });
        return { success: false, userErrorMsg: 'Oops, something went wrong. Please retry later.' };
      }

      setAvatarUrl(getMediaUrl(mediaData.filePath));
      setImageStatus('success');
      refetchCharacters();

      storage.setItem(LS_NEW_CHAR_KEY, JSON.stringify({
        ...state, // keep the same state for faster re-creation, but reset the step
        current_step: 0,
      }));
      return { success: true, data: mediaData };
      // setImageRequestId(mediaData?.requestId || null);
    }

    const generatedCharacters = characters?.filter(c => !c.parent_char_id && c.character_scope === 'private') || [];
    const isFirstCharacter = generatedCharacters.length === 0;
    const cost = isFirstCharacter ? 0 : CUSTOM_CHAR_COST;
    const hasEnoughGems = balance.total >= cost;

    const handleConfirm = async () => {
      if (!user) {
        authDialogOnCloseHandlerRef.current = handleAuthDialogOnClose;
        setShowAuthDialog(true);
        return;
      }

      if (!isSubscriber && generatedCharacters.length >= FREE_CHAR_LIMIT) {
        setShowUpgradeDialog(true);
        return;
      }
      if (isSubscriber && !hasEnoughGems) {
        if (showGemBalanceMsg) { // already clicked
          window?.open('/gems', '_blank');
          return;
        }
        setShowGemBalanceMsg(true);
        return;
      }
      setShowGemBalanceMsg(false);

      setCharacterCreationError('');
      setIsSubmitting(true);
      try {
        // Call eh-new-char edge function
        let ethnicity = state.attributes.ethnicity;
        if (ethnicity === 'random') {
          const choices = ETHNICITY_OPTIONS.filter(o => o.value !== 'random');
          ethnicity = choices[Math.floor(Math.random() * choices.length)].value;
          if (import.meta.env.DEV) console.log('[DEV] random ethnicity', ethnicity);
          // Don't modify state: we keep random ethnicity for next generation
          // setState(prev => ({ ...prev, attributes: { ...prev.attributes, ethnicity } }));
        }
        const { data, error } = await supabase.functions.invoke('eh-new-char', {
          body: { attributes: { ...state.attributes, ethnicity, user_id: user.id } }
        });
        if (error || !data) {
          setIsSubmitting(false);
          if (import.meta.env.DEV) console.log('[DEV] eh-new-char error', error);
          setCharacterCreationError('Failed to create your AI. Please try again.');
          return;
        }
        if (data.flaggedAttributes && data.flaggedAttributes.length > 0) {
          setIsSubmitting(false);
          setCharacterCreationError('Some attributes include prohibited content: '
            + (data.flaggedAttributes as string[]).map(a => a.replace(/_/g, ' ')).join(', '));
          return;
        }

        await refreshBalance();

        setCreatedCharacter(data.character as Database['eh']['Tables']['characters']['Row']);
        setShowSummaryDialog(true);
        setIsSubmitting(false);
        setImageStatus('pending');
        setAvatarUrl(null);

        // Subscribe to media_requests for status updates if needed
        // const sub = supabase
        //   .channel('eh-media-requests')
        //   .on(
        //     'postgres_changes',
        //     {
        //       event: 'UPDATE',
        //       schema: 'eh',
        //       table: 'media_requests',
        //       filter: `resource_id=eq.${data.character.id}`
        //     },
        //     async (payload: { new: Database['eh']['Tables']['media_requests']['Row'] & { filePath?: string } }) => {
        //       console.log('media_requests update', payload);
        //       const media = payload.new as Database['eh']['Tables']['media_requests']['Row'] & { filePath?: string };
        //       if (media?.media_type === 'profile_image') {
        //         if (media.status === 'success') {
        //           setImageStatus('success');
        //           const { data: charData, error: charError } = await supabaseEh.from('characters')
        //             .select('profile_media_fpaths')
        //             .eq('id', data.character.id)
        //             .single();
        //           if (charData && charData.profile_media_fpaths && charData.profile_media_fpaths.length > 0) {
        //             setAvatarUrl(getMediaUrl(charData.profile_media_fpaths[0]));
        //             storage.setItem(LS_NEW_CHAR_KEY, JSON.stringify({
        //               ...state, // keep the same state for faster re-creation, but reset the step
        //               current_step: 0,
        //             }));
        //           }
        //           if (charError) {
        //             trackEvent(UI_EVENTS.UI_ERROR, {
        //               error: charError,
        //               source: 'CreateCharacterPage',
        //             });
        //           }
        //           sub.unsubscribe();
        //         } else if (media.status === 'failed') {
        //           setImageStatus('failed');
        //           sub.unsubscribe();
        //         } else if (media.status === 'pending') {
        //           setImageStatus('pending');
        //         }
        //       }
        //     }
        //   )
        //   .subscribe();

        await handleMediaRequest(data.imagePrompt, data.character.id);
      } catch (err) {
        setIsSubmitting(false);
        setCharacterCreationError('Failed to create character: ' + (err instanceof Error ? err.message : String(err)));
      } finally {
        // restore default handler
        authDialogOnCloseHandlerRef.current = (open) => { setShowAuthDialog(open); };
      }
    };
    const handleStartChat = () => {
      if (createdCharacter?.url_slug) {
        navigate(`/c/${createdCharacter.url_slug}`);
      } else { // should never happen
        setShowSummaryDialog(false);
        trackEvent(UI_EVENTS.UI_ERROR, {
          error: `Failed to create chat for ${createdCharacter?.id}: No URL slug`,
          source: 'CreateCharacterPage',
        });
      }
    };

    return (
      <div className="w-full flex flex-col gap-8">
        {isSubmitting ? (
          <div className="flex flex-col items-center justify-center">
            <div className="w-16 h-16 border-4 border-zinc-600 border-t-pink-500 rounded-full animate-spin mb-4"></div>
            <p className="text-zinc-300 text-center px-6 text-xl">
              <span>Creating your </span>
              <span className="font-bold">unique AI 🥰</span>
            </p>
          </div>
        ) : (
          <div>
            <div className="text-white text-center font-semibold mb-6 text-2xl">Review & Confirm</div>
            <div className="flex gap-2 sm:gap-4 justify-center flex-wrap gap-4 sm:gap-8 mb-8 place-content-center">
              {summary.filter(s => s.show === undefined || s.show).map(s => (
                <div key={s.label} className="flex flex-col items-center select-none">
                  <div className="text-xs text-zinc-400 mb-1 font-medium">{s.label}</div>
                  <div className="relative w-28 h-32 rounded-lg overflow-hidden bg-zinc-700 flex items-center justify-center text-white text-center">
                    {getImgPath(s.value, s.options, state.attributes.gender) ? (
                       <>
                        <img
                          src={`/images/demo/${getImgPath(s.value, s.options, state.attributes.gender)}`}
                          alt={s.label}
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 rounded-md text-white text-sm">
                          {getLabel(s.value, s.options)}
                        </span>
                       </>
                    ) : (
                      <span className="text-white font-bold text-lg">{getLabel(s.value, s.options)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div>
                <div className="text-xs text-zinc-400 mb-1">Personality</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.personality, PERSONALITY_OPTIONS) || state.attributes.personality
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Relationship</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.relationship, RELATIONSHIP_OPTIONS) || state.attributes.relationship
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Occupation</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.occupation, OCCUPATION_OPTIONS) || state.attributes.occupation
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Hobbies</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  Array.isArray(state.attributes.hobbies) ? state.attributes.hobbies.join(', ') : state.attributes.hobbies
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Outfit</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.outfit, OUTFIT_OPTIONS) || state.attributes.outfit
                }</div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1">Voice</div>
                <div className="bg-zinc-700 rounded-lg p-2 text-white min-h-[40px]">{
                  getLabel(state.attributes.voice, VOICE_OPTIONS) || state.attributes.voice
                }</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-4 mt-6">
              {characterCreationError && (
                <div className="text-red-500 text-center">{characterCreationError}</div>
              )}
              
              {/* Gem cost information */}
              {/* {user && (
                (() => {
                  if (cost > 0 && !hasEnoughGems && showGemBalanceMsg) {
                    if (import.meta.env.DEV) console.log(`[DEV] balance: ${balance.total} 💎, cost: ${cost} 💎`);
                    return (
                      <div className={`text-center px-4 py-2 rounded ${hasEnoughGems ? 'bg-zinc-700' : 'bg-rose-900'}`}>
                        <div className="text-sm text-zinc-300">
                          <span>Character creation costs </span>
                          <span className="font-semibold">{`${cost} 💎`}</span>
                          <span>{`, you have ${balance.total} 💎`}</span>
                        </div>
                        {!hasEnoughGems && (
                          <div className="text-red-400 text-sm mt-1">
                            <span className="text-blue-400 cursor-pointer underline" onClick={() => setShowUpgradeDialog(true)}>
                              Get more gems
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  } else if (cost > 0 || isLoadingUserInfo) {
                    return null;
                  }
                  
                  return (
                    <div className="text-center px-4 py-2 rounded bg-green-900">
                      <div className="text-sm text-green-300">
                        <span>🎉 First character is free!</span>
                      </div>
                    </div>
                  );
                })()
              )} */}

              <button
                className="px-8 py-3 rounded bg-pink-600 text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConfirm}
                disabled={missing || isSubmitting || (!hasEnoughGems && showGemBalanceMsg)}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Creating</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center   gap-2">
                      <span>Confirm & Create</span>
                      {(cost > 0 && !isLoadingUserInfo) && isSubscriber && (
                        <span className="text-sm bg-pink-700/80 rounded-md px-2 py-1">{`${cost} 💎`}</span>
                      )}
                    </div>
                    {!isLoadingUserInfo && (
                      <div className="text-xs">
                        {cost === 0 ? (<span>First character is free!</span>) : (!hasEnoughGems && showGemBalanceMsg) && (
                          <span
                            tabIndex={0}
                            role="button"
                            className="cursor-pointer underline"
                            onClick={() => {
                              trackEvent(PAYMENT_EVENTS.GEM_BALANCE_MSG_CLICKED, {
                                source: 'CreateCharacterPage.submitButton',
                                message: 'Get more gems',
                              });
                              window?.open('/gems', '_blank');
                            }}>
                            Get more gems
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </button>
              {!hasEnoughGems && generatedCharacters.length && (
                <Button variant="link" asChild className="text-xs -mt-4">
                  <Link to={`/c/${generatedCharacters[0].url_slug}`}>
                    <ArrowRight className="size-3" />
                    <span>or chat with your free character</span>
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}
        {createdCharacter && (
          <CharacterCreationSuccessDialog
            open={showSummaryDialog}
            onOpenChange={(open) => {
              setShowSummaryDialog(open);
              if (!open) goToStep(0)
            }}
            character={createdCharacter}
            imageStatus={imageStatus}
            avatarUrl={avatarUrl}
            onStartChat={handleStartChat}
            onRetryImage={handleMediaRequest}
            onCharacterSave={() => {
              refetchCharacters();
            }}
          />
        )}
      </div>
    );
  };

  // Set defaults for each step on mount/step change
  useEffect(() => {
    const setDefault = (key: string, options: OptionsType[]) => {
      const availableOptions = options.filter(genderFilter);
      if (!state.attributes[key]) {
        const def = availableOptions.find(o => o.isDefault)
          || availableOptions[Math.floor(Math.random() * availableOptions.length)];
        if (def) {
          handleSelect(key, def.value);
          return def;
        };
      }
    };
    if (state.current_step === 0) {
      setDefault('gender', GENDER_OPTIONS);
      setDefault('style', STYLE_OPTIONS);
    } else if (state.current_step === 1) {
      setDefault('ethnicity', ETHNICITY_OPTIONS);
      setDefault('age', AGE_OPTIONS);
    } else if (state.current_step === 2) {
      setDefault('eye_color', EYE_COLOR_OPTIONS);
      setDefault('hair_style', HAIR_STYLE_OPTIONS);
      setDefault('hair_color', HAIR_COLOR_OPTIONS);
    } else if (state.current_step === 3) {
      setDefault('body_type', BODY_TYPE_OPTIONS);
      if (state.attributes.gender === 'f') setDefault('bust_size', BUST_SIZE_OPTIONS);
      if (state.attributes.gender === 'f') setDefault('butt_size', BUTT_SIZE_OPTIONS);
    } else if (state.current_step === 4) {
      const defPers = setDefault('personality', PERSONALITY_OPTIONS);
      const defRel = setDefault('relationship', RELATIONSHIP_OPTIONS);
      if (!personalityCustom) setPersonalityCustom(defPers ? defPers.label : '');
      if (!relationshipCustom) setRelationshipCustom(defRel ? defRel.label : '');
    } else if (state.current_step === 5) {
      const defOcc = setDefault('occupation', OCCUPATION_OPTIONS);
      if (!occupationCustom) setOccupationCustom(defOcc ? defOcc.label : '');
      const defHobbies = HOBBY_OPTIONS.filter(o => o.isDefault).map(o => o.value);
      if ((!state.attributes.hobbies || state.attributes.hobbies.length === 0) && defHobbies.length > 0) handleSelect('hobbies', defHobbies);
      if (!hobbiesCustom) setHobbiesCustom(defHobbies.join(', '));
    } else if (state.current_step === 6) {
      const defOutfit = setDefault('outfit', OUTFIT_OPTIONS);
      if (!outfitCustom) setOutfitCustom(defOutfit ? defOutfit.label : '');
      setDefault('voice', VOICE_OPTIONS);
    }
    // Add similar logic for future steps
    // eslint-disable-next-line
  }, [genderFilter, state.current_step, state.attributes.gender]);

  // Stop all voice samples
  const stopAllVoiceSamples = () => {
    Object.entries(voiceAudioRefs.current).forEach(([voiceValue, audio]) => {
      const playPromise = voicePlayPromiseRefs.current[voiceValue];
      
      if (playPromise) {
        // Wait for play promise before pausing
        playPromise
          .then(() => audio.pause())
          .catch(() => audio.pause()); // If play failed, safe to pause anyway
      } else {
        audio.pause();
      }
      
      voicePlayPromiseRefs.current[voiceValue] = null;
    });
    setPlayingVoice(null);
  };

  // Cleanup voice samples on unmount
  useEffect(() => {
    return () => {
      stopAllVoiceSamples();
      voicePlayPromiseRefs.current = {};
    };
  }, []);

  // Card UI shell
  return (
    <div className="flex flex-col">
      <SEO
        title="Create Your Own AI Girlfriend or companion - EveningHoney.AI"
        description="Create your own AI girlfriend, wife, colleage, or dreamy companion with EveningHoney.AI. Customize every detail to create the perfect AI to chat with."
        ogUrl="/create-character"
      />
      <div className="flex-1 flex flex-col items-center justify-center bg-zinc-900 py-4 sm:py-8 px-1">
        <h1 ref={titleRef} className={`text-2xl sm:text-3xl font-bold text-white mb-2 ${isSubmitting ? 'invisible' : ''}`}>
          Create your own AI
        </h1>
        {renderTimeline()}
        <div className="w-full max-w-2xl bg-zinc-800 rounded-lg shadow-lg mt-2 pt-4 px-4 sm:px-8 pb-8 flex flex-col items-center">
          {state.current_step < 7 && ( // single choice in step 8 (review)
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4">{stepTitles[state.current_step]}</h2>
          )}
          {/* Step content */}
          <div className="w-full flex flex-col items-center justify-center min-h-[300px]">
            {state.current_step === 0 ? renderStep1() :
              state.current_step === 1 ? renderStep2() :
              state.current_step === 2 ? renderStep3() :
              state.current_step === 3 ? renderStep4() :
              state.current_step === 4 ? renderStep5() :
              state.current_step === 5 ? renderStep6() :
              state.current_step === 6 ? renderStep7() :
              state.current_step === 7 ? renderStep8() : (
              <span className="text-zinc-400">Step {state.current_step + 1} content goes here.</span>
            )}
          </div>
          <div className={`flex w-full justify-between mt-8 ${isSubmitting ? 'hidden' : ''}`}>
            <button
              className="px-4 py-2 rounded bg-zinc-700 text-white disabled:opacity-50 select-none"
              onClick={prevStep}
              disabled={state.current_step === 0}
            >
              Previous
            </button>
            <button
              className={`px-4 py-2 rounded bg-pink-600 text-white disabled:opacity-50 select-none ${state.current_step === 7 ? 'hidden' : ''}`}
              onClick={nextStep}
              disabled={
                state.current_step === TOTAL_STEPS - 1 ||
                (state.current_step === 0 && (!gender || !style)) ||
                (state.current_step === 1 && (!ethnicity || !age)) ||
                (state.current_step === 2 && (!state.attributes.eye_color || !state.attributes.hair_style || !state.attributes.hair_color)) ||
                (state.current_step === 3 && (!body_type || (gender === 'f' && !(bust_size && butt_size)))) ||
                (state.current_step === 4 && (!state.attributes.personality || !state.attributes.relationship)) ||
                (state.current_step === 5 && (!state.attributes.occupation || !state.attributes.hobbies/* || state.attributes.hobbies.length === 0*/)) ||
                (state.current_step === 6 && (!state.attributes.outfit || !state.attributes.voice))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
      <EhAuthDialog
          open={showAuthDialog}
          onOpenChange={authDialogOnCloseHandlerRef.current}
          onOnboardingOpenChange={authDialogOnCloseHandlerRef.current}
        />
      <EhUpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        limitType="create"
        onUpgradeClick={() => {
          if (!user) {
            setShowAuthDialog(true);
            return false;
          }
          return true;
        }}
      />
    </div>
  );
} 